import { useState } from 'react';
import { Box, FormControlLabel, Link, Switch } from '@mui/material';
import { ConditionalLoader, Section } from 'dashboard409a/pages/Operations/components';
import Avatar from 'dashboard409a/pages/Operations/components/Avatar/Avatar';
import formatCurrency from 'dashboard409a/utilities/formatCurrency';
import { DEFAULT_VISIBLE_FIELDS, getFinancialFields } from './ValuedInformation.constants';
import { ValuedInformationProps } from './ValuedInformation.props';
import { Container, Field, FinancialField, FinancialFieldsGrid, HeaderRow, Label, PersonnelRow, StatusBadge, StyledLinkSpan, ToggleContainer, UserSection, Value } from './ValuedInformation.styles';

const ValuedInformation = ({ valuedInformation, isLoading }: ValuedInformationProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const allFields = getFinancialFields(valuedInformation);

  // Filter fields based on toggle state
  const visibleFields = allFields.filter(field =>
    isExpanded || DEFAULT_VISIBLE_FIELDS.includes(field.key as typeof DEFAULT_VISIBLE_FIELDS[number])
  );

  return (
    <Section title="Valuation">
      <Container>
        <HeaderRow>
          <Field>
            <ConditionalLoader isLoading={isLoading} width={200}>
              <Link href={valuedInformation?.valuationUrl} target="_blank" rel="noopener noreferrer">
                <StyledLinkSpan>{valuedInformation?.valuationName || '-'}</StyledLinkSpan>
              </Link>
            </ConditionalLoader>
          </Field>
          <ConditionalLoader isLoading={isLoading} width={200}>
            <StatusBadge>{valuedInformation?.bookingType || '-'}</StatusBadge>
          </ConditionalLoader>
        </HeaderRow>
        {valuedInformation?.valuationNotice && (
          <span>NOTICE: {valuedInformation.valuationNotice}</span>
        )}
        <Box mt={1} />
        <ToggleContainer>
          <FormControlLabel
            control={
              <Switch
                checked={isExpanded}
                onChange={e => setIsExpanded(e.target.checked)}
                size="small"
              />
            }
            label={isExpanded ? 'Show Less' : 'Show More'}
          />
        </ToggleContainer>
        <FinancialFieldsGrid>
          {visibleFields.map(field => {
            const value = field.getValue();
            let displayValue: string = '-';
            if (field.isCurrency) {
              if (value !== undefined && value !== null && value !== '-') {
                displayValue = formatCurrency({
                  value: typeof value === 'number' ? value : Number(value) || 0,
                });
              }
            } else {
              displayValue = (value as string) || '-';
            }
            return (
              <FinancialField key={field.key}>
                <Label>{field.label}</Label>
                <ConditionalLoader isLoading={isLoading} width={200}>
                  <Value>{displayValue}</Value>
                </ConditionalLoader>
              </FinancialField>
            );
          })}
        </FinancialFieldsGrid>
        <PersonnelRow>
          <Field>
            <Label>Valuation Lead</Label>
            <UserSection>
              <Avatar
                src={valuedInformation?.valuationLeadImageSrc || ''}
                name={valuedInformation?.valuationLeadName || '-'}
                roleTitle="Valuation Lead"
                isLoading={isLoading}
              />
            </UserSection>
          </Field>
          <Field>
            <Label>Engagement Manager</Label>
            <UserSection>
              <Avatar
                src={valuedInformation?.valuationEngagementManagerImageSrc || ''}
                name={valuedInformation?.valuationEngagementManager || '-'}
                roleTitle={valuedInformation?.valuationEngagementManagerRole || '-'}
                isLoading={isLoading}
              />
            </UserSection>
          </Field>
        </PersonnelRow>
      </Container>
    </Section>
  );
};

export default ValuedInformation;
